import React, { useEffect, useState } from 'react';
import CustomTable, { Column, IPageRequest } from '../../components/CustomTable';
import { useNavigate } from 'react-router-dom';
import { getSuppliers } from '../../services/supplierService';
import { useTranslation } from 'react-i18next';
import { RequestStatus } from '../../types/enums';

const SupplierRequests: React.FC = () => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [pageCount, setPageCount] = useState(1);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [status, setStatus] = useState<RequestStatus | null>(null);

  const pageRequest: IPageRequest = {
    pageNumber: currentPage + 1,
    pageSize: itemsPerPage,
  };

  const navigate = useNavigate();

  const columns: Column[] = [
    {
      title: t('companyName', 'Company Name'),
      dataKey: 'businessName',
      type: 'string',
    },
    { title: t('email', 'Email'), dataKey: 'email', type: 'string' },
    { title: t('country', 'Country'), dataKey: 'country', type: 'string' },
    { title: t('location', 'Location'), dataKey: 'city', type: 'string' },
    { title: t('status', 'Status'), dataKey: 'status', type: 'status' },
    {
      title: t('actions', 'Actions'),
      dataKey: 'actions', // This key is used but not directly needed for rendering
      type: 'action',
      actions: [
        {
          label: t('view', 'View'),
          className: '',
          action: (item: any) => handleView(item),
          status: true,
        },
      ],
    },
  ];

  const statusOptions = [
    { label: 'All', action: () => statusChange(null) },
    { label: 'New', action: () => statusChange(RequestStatus.NEW) },
    { label: 'More Info', action: () => statusChange(RequestStatus.MORE_INFORMATION) },
    {
      label: 'Processing',
      action: () => statusChange(RequestStatus.PROCESSING),
    },
    {
      label: 'Approved',
      action: () => statusChange(RequestStatus.APPROVED),
    },
    {
      label: 'Rejected',
      action: () => statusChange(RequestStatus.REJECTED),
    },
  ];

  const statusChange = (value: RequestStatus | null) => {
    setStatus(value);
    pageRequest.pageNumber = 1;
    fetchSuppliers(value);
  };

  const handleView = (item: any) => {
    navigate(`/supplier-registration?supplierId=${item.companyId}`, {
      state: {
        isSupplier: false,
        requestStatus: status,
      },
    });
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    pageRequest.pageNumber = 1;
    fetchSuppliers(status, value);
  };

  const fetchSuppliers = async (
    status: RequestStatus | null,
    supplierName: string | null = null
  ) => {
    supplierName = supplierName ?? searchValue;
    const params: any = {
      ...pageRequest,
      ...(status !== null && { status }),
      ...(supplierName?.trim() && { supplierName }),
    };
    const response = await getSuppliers(params);
    if (response) {
      // const filteredSuppliers = response.data.filter((supplier: any) => supplier.status === 'New');
      // setSuppliers(filteredSuppliers);
      setSuppliers(response.data);
      if (response?.totalRecords) {
        setPageCount(Math.ceil(response.totalRecords / itemsPerPage));
      }
    }
  };

  const handlePageChange = (selectedPage: number) => {
    setCurrentPage(selectedPage);
  };

  useEffect(() => {
    fetchSuppliers(status);
  }, [currentPage, itemsPerPage]);

  return (
    <div>
      <CustomTable
        data={suppliers}
        columns={columns}
        pageCount={pageCount} // Pass the total page count
        showPagination={true} // Control whether to show pagination
        showSearch={true} // Control whether to show the search box
        onSearch={handleSearch} // Pass the search handler
        showAddButton={false}
        showTitle={true}
        title={t('supplierRequests', 'Supplier Requests')}
        onPageChange={(selectedPage) => handlePageChange(selectedPage)}
        showFilterIcon={false}
        showStatusFilter={true}
        statusOptions={statusOptions}
      />
    </div>
  );
};

export default SupplierRequests;
